import { Component } from '@angular/core';

@Component({
  selector: 'app-proposal',
  standalone: true,
  template: `
    <main class="proposal-page proposal-saas">
      <nav class="proposal-nav" aria-label="Proposal header">
        <a class="proposal-brand" href="/proposal" aria-label="HIFI Infotech proposal">
          <img src="assets/full hifi logo.png" alt="HIFI Infotech">
        </a>
        <div class="proposal-nav-actions">
          <a href="#scope">Scope</a>
          <a href="#commercial">Commercials</a>
          <button class="proposal-pdf-button" type="button" (click)="generatePdf()">
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="M6 2h9l5 5v15H6V2Zm8 1.5V8h4.5L14 3.5ZM8 4v16h10V10h-6V4H8Zm1.5 9h2.2c1.5 0 2.5 1 2.5 2.4s-1 2.4-2.5 2.4h-.8V20H9.5v-7Zm1.4 1.3v2.2h.7c.7 0 1.1-.4 1.1-1.1s-.4-1.1-1.1-1.1h-.7Zm4.1-1.3h3.5v1.3h-2.1v1.6h1.8v1.3h-1.8V20H15v-7Z" />
            </svg>
            Generate PDF
          </button>
          <a class="proposal-contact" href="https://www.hifiinfotech.com" target="_blank" rel="noopener noreferrer">
            www.hifiinfotech.com
          </a>
        </div>
      </nav>

      <section class="proposal-hero">
        <div class="proposal-hero-copy">
          <span class="proposal-kicker">Project Proposal</span>
          <h1>PERKS Sports Academy Management System</h1>
          <p>
            Run admissions, guest play, attendance and fees from one clean operating desk.
            Built for academies handling Swimming, Cricket, Football, Badminton, Tennis,
            Karate, Yoga, and more.
          </p>
          <div class="proposal-actions">
            <a href="#commercial">View Commercials</a>
            <a href="#scope">Explore Scope</a>
          </div>
        </div>

        <aside class="proposal-summary" aria-label="Proposal summary">
          <img src="assets/hifi logo.png" alt="HIFI Infotech">
          <span>Prepared by</span>
          <strong>HIFI Infotech</strong>
          <a href="https://www.hifiinfotech.com" target="_blank" rel="noopener noreferrer">www.hifiinfotech.com</a>
          <dl>
            <div>
              <dt>Proposal Date</dt>
              <dd>23 April 2026</dd>
            </div>
            <div>
              <dt>Client</dt>
              <dd>PERKS Sports Academy</dd>
            </div>
            <div>
              <dt>Solution Type</dt>
              <dd>Web SaaS Admin System</dd>
            </div>
          </dl>
        </aside>
      </section>

      <section class="proposal-metrics" aria-label="Proposal highlights">
        @for (metric of metrics; track metric.label) {
          <article>
            <span>{{ metric.value }}</span>
            <p>{{ metric.label }}</p>
          </article>
        }
      </section>

      <section class="proposal-section proposal-overview">
        <div>
          <span class="proposal-section-label">Project Overview</span>
          <h2>Sports academy operations, digitized end to end.</h2>
        </div>
        <p>
          This Sports Academy Management System streamlines operations for multi-sport academies.
          Outcome: less manual work, faster operations, and a single source of truth for staff and management.
        </p>
      </section>

      <section class="proposal-section">
        <div class="proposal-section-heading">
          <div>
            <span class="proposal-section-label">System Handles</span>
            <h2>Daily academy workflows covered</h2>
          </div>
        </div>
        <div class="proposal-list-grid">
          @for (item of systemHandles; track item) {
            <span>{{ item }}</span>
          }
        </div>
      </section>

      <section class="proposal-section proposal-objective">
        <div>
          <span class="proposal-section-label">Objectives</span>
          <h2>Reduce manual work and improve operational visibility.</h2>
        </div>
        <ul class="proposal-check-list">
          @for (objective of objectives; track objective) {
            <li>{{ objective }}</li>
          }
        </ul>
      </section>

      <section class="proposal-section" id="scope">
        <div class="proposal-section-heading">
          <div>
            <span class="proposal-section-label">Scope</span>
            <h2>Core modules included</h2>
          </div>
          <p>Module structure is aligned to the current demo application and can be expanded after client confirmation.</p>
        </div>
        <div class="proposal-feature-grid">
          @for (feature of features; track feature.title) {
            <article class="proposal-feature">
              <span>{{ feature.code }}</span>
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.description }}</p>
            </article>
          }
        </div>
      </section>

      <section class="proposal-band">
        <div>
          <span class="proposal-section-label">SaaS Experience</span>
          <h2>Modern, secure, and ready for operational scale.</h2>
          <p>
            The platform will follow a clean SaaS admin design with protected routing, reusable modules,
            Angular frontend, ASP.NET Core Web API backend, SQL Server database, QR integration,
            production build support, and a branded HIFI Infotech provider footer.
          </p>
        </div>
        <ul class="proposal-stack">
          <li>Angular frontend application</li>
          <li>ASP.NET Core Web API</li>
          <li>SQL Server database schema</li>
          <li>QR guest pass workflow</li>
          <li>Role-based access control</li>
          <li>Production deployment build</li>
        </ul>
      </section>

      <section class="proposal-section">
        <div class="proposal-section-heading">
          <div>
            <span class="proposal-section-label">Deliverables</span>
            <h2>What HIFI Infotech will hand over</h2>
          </div>
        </div>
        <div class="proposal-deliverables">
          @for (deliverable of deliverables; track deliverable) {
            <article>
              <span class="proposal-deliverable-icon" [attr.aria-label]="deliverable.title">
                <svg aria-hidden="true"><use [attr.href]="'#proposal-icon-' + deliverable.icon"></use></svg>
              </span>
              <p>{{ deliverable.title }}</p>
            </article>
          }
        </div>
      </section>

      <section class="proposal-section">
        <div class="proposal-section-heading">
          <div>
            <span class="proposal-section-label">Delivery Plan</span>
            <h2>Implementation phases</h2>
          </div>
        </div>
        <div class="proposal-timeline">
          @for (phase of phases; track phase.name) {
            <article>
              <span>{{ phase.step }}</span>
              <h3>{{ phase.name }}</h3>
              <p>{{ phase.description }}</p>
              <strong>{{ phase.duration }}</strong>
            </article>
          }
        </div>
      </section>

      <section class="proposal-section proposal-commercial" id="commercial">
        <div>
          <span class="proposal-section-label">Commercial Proposal</span>
          <h2>Cost, payment, and approval placeholders</h2>
          <p>
            Final pricing will be locked after confirmation of exact forms, backend scope, deployment environment,
            integrations, reports, and post-launch support expectations.
          </p>
        </div>
        <div class="proposal-commercial-card">
          <div>
            <span>Project Cost</span>
            <strong>Rs. __________</strong>
          </div>
          <div>
            <span>Advance Payment</span>
            <strong>___%</strong>
          </div>
          <div>
            <span>Estimated Duration</span>
            <strong>___ working days</strong>
          </div>
        </div>
      </section>

      <section class="proposal-section proposal-mockups">
        <div>
          <span class="proposal-section-label">UI Mockups</span>
          <h2>Screen previews are part of the proposal flow.</h2>
        </div>
        <p>
          Login, dashboard overview, and module mockups are used to visualize the final user interface
          and workflow. Final UI can be refined during implementation based on client feedback.
        </p>
      </section>

      <section class="proposal-section proposal-notes">
        <div>
          <span class="proposal-section-label">Assumptions</span>
          <h2>Items to confirm before kickoff</h2>
        </div>
        <ul>
          <li>Client will confirm final fields, forms, roles, reports, and business rules.</li>
          <li>Backend APIs, WhatsApp or SMS, payment gateway, and advanced reports can be added as separate scope.</li>
          <li>Hosting, domain, SSL, and third-party service charges are separate unless included in the agreement.</li>
          <li>Post-delivery support and maintenance can be provided through a separate plan.</li>
        </ul>
      </section>

      <section class="proposal-section proposal-contact-card">
        <div>
          <span class="proposal-section-label">Contact Details</span>
          <h2>Mariappan Raja</h2>
          <p>Hifi Infotech</p>
        </div>
        <div>
          <a href="tel:+918344617612">8344617612</a>
          <a href="mailto:info@hifiinfotech.com">info@hifiinfotech.com</a>
          <a href="https://www.hifiinfotech.com" target="_blank" rel="noopener noreferrer">www.hifiinfotech.com</a>
        </div>
      </section>

      <footer class="proposal-footer">
        <div>
          <img src="assets/full hifi logo.png" alt="HIFI Infotech">
          <a href="https://www.hifiinfotech.com" target="_blank" rel="noopener noreferrer">www.hifiinfotech.com</a>
        </div>
        <p>Reduce manual work, improve tracking, enhance customer experience, and increase revenue visibility.</p>
      </footer>

      <svg class="proposal-icon-sprite" aria-hidden="true">
        <symbol id="proposal-icon-dashboard" viewBox="0 0 24 24">
          <path d="M4 13h7V4H4v9Zm0 7h7v-5H4v5Zm9 0h7v-9h-7v9Zm0-16v5h7V4h-7Z" />
        </symbol>
        <symbol id="proposal-icon-api" viewBox="0 0 24 24">
          <path d="M7.4 7.8 3.2 12l4.2 4.2 1.4-1.4L6 12l2.8-2.8-1.4-1.4Zm9.2 0-1.4 1.4L18 12l-2.8 2.8 1.4 1.4 4.2-4.2-4.2-4.2ZM10 18h2.1L14 6h-2.1L10 18Z" />
        </symbol>
        <symbol id="proposal-icon-database" viewBox="0 0 24 24">
          <path d="M12 3C7.6 3 4 4.3 4 6v12c0 1.7 3.6 3 8 3s8-1.3 8-3V6c0-1.7-3.6-3-8-3Zm0 2c3.6 0 5.8.8 6 1-.2.2-2.4 1-6 1s-5.8-.8-6-1c.2-.2 2.4-1 6-1Zm0 14c-3.6 0-5.8-.8-6-1v-2.4c1.5.9 3.9 1.4 6 1.4s4.5-.5 6-1.4V18c-.2.2-2.4 1-6 1Zm0-4c-3.6 0-5.8-.8-6-1v-2.4c1.5.9 3.9 1.4 6 1.4s4.5-.5 6-1.4V14c-.2.2-2.4 1-6 1Zm0-4c-3.6 0-5.8-.8-6-1V8.6C7.5 9.5 9.9 10 12 10s4.5-.5 6-1.4V10c-.2.2-2.4 1-6 1Z" />
        </symbol>
        <symbol id="proposal-icon-qr" viewBox="0 0 24 24">
          <path d="M4 4h7v7H4V4Zm2 2v3h3V6H6Zm7-2h7v7h-7V4Zm2 2v3h3V6h-3ZM4 13h7v7H4v-7Zm2 2v3h3v-3H6Zm8-2h2v2h-2v-2Zm2 2h2v2h-2v-2Zm-4 2h2v3h-2v-3Zm6 1h2v2h-2v-2Zm-2 2h2v2h-2v-2Zm4-7h-2v2h2v-2Z" />
        </symbol>
        <symbol id="proposal-icon-reports" viewBox="0 0 24 24">
          <path d="M5 3h14v18H5V3Zm3 4v2h8V7H8Zm0 4v2h5v-2H8Zm0 4v2h3v-2H8Zm7 2h2v-5h-2v5Zm-3 0h2v-3h-2v3Z" />
        </symbol>
        <symbol id="proposal-icon-deploy" viewBox="0 0 24 24">
          <path d="M12 2 4 6v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V6l-8-4Zm0 2.2 6 3V12c0 3.8-2.4 7.4-6 8.8-3.6-1.4-6-5-6-8.8V7.2l6-3ZM11 7h2v6h3l-4 4-4-4h3V7Z" />
        </symbol>
      </svg>
    </main>
  `
})
export class ProposalComponent {
  generatePdf(): void {
    window.print();
  }

  readonly metrics = [
    { value: '8+', label: 'Sports and academy activities supported' },
    { value: 'QR', label: 'Guest pass and scan workflow' },
    { value: 'SaaS', label: 'Clean web admin experience' },
    { value: 'API', label: 'ASP.NET Core backend ready' }
  ];

  readonly systemHandles = [
    'Student enrollment - regular and camp',
    'Guest play hourly bookings',
    'Student and staff attendance tracking',
    'Fees and payment management',
    'QR-based entry and exit system',
    'Reports and analytics'
  ];

  readonly objectives = [
    'Digitize complete academy operations',
    'Track student and staff attendance accurately',
    'Enable easy guest play booking management',
    'Simplify fee collection and tracking',
    'Provide real-time reports and dashboards',
    'Enable QR-based entry and exit tracking'
  ];

  readonly features = [
    {
      code: '01',
      title: 'Admin Login',
      description: 'Secure access for authorized academy users with protected internal pages.'
    },
    {
      code: '02',
      title: 'Dashboard',
      description: 'Operational overview with quick access to academy activity and key modules.'
    },
    {
      code: '03',
      title: 'Student Management',
      description: 'Add, edit, delete, assign sport, batch, level, monthly subscription, and active status.'
    },
    {
      code: '04',
      title: 'Guest Play',
      description: 'Walk-in registration, hourly booking, temporary QR pass, in-time and out-time tracking.'
    },
    {
      code: '05',
      title: 'Summer Camp',
      description: 'Camp registration for 7, 15, or 30 days with trainer, batch, attendance, and fee tracking.'
    },
    {
      code: '06',
      title: 'Attendance',
      description: 'Track student and staff attendance with daily operational visibility.'
    },
    {
      code: '07',
      title: 'Fees & Receipts',
      description: 'Sport-wise fee setup, monthly tracking, guest charges, partial payments, receipts, and due alerts.'
    },
    {
      code: '08',
      title: 'Staff Management',
      description: 'Maintain staff details, roles, contact data, and assigned responsibilities.'
    },
    {
      code: '09',
      title: 'Reports',
      description: 'Total students, guests, staff, daily attendance, revenue, pending fees, and sport-wise analytics.'
    },
    {
      code: '10',
      title: 'Settings',
      description: 'Configure academy-level options and prepare the system for future customization.'
    }
  ];

  readonly deliverables = [
    { title: 'Complete web application with admin dashboard', icon: 'dashboard' },
    { title: 'Backend API using ASP.NET Core Web API', icon: 'api' },
    { title: 'SQL Server database schema', icon: 'database' },
    { title: 'QR code integration for ID generation and scan flow', icon: 'qr' },
    { title: 'Reports and dashboard screens', icon: 'reports' },
    { title: 'Deployment support', icon: 'deploy' }
  ];

  readonly phases = [
    {
      step: 'Phase 1',
      name: 'Discovery & Confirmation',
      description: 'Finalize module fields, business rules, roles, and screen flow.',
      duration: '___ days'
    },
    {
      step: 'Phase 2',
      name: 'Design & Frontend Build',
      description: 'Polish SaaS UI, implement modules, and prepare reusable workflows.',
      duration: '___ days'
    },
    {
      step: 'Phase 3',
      name: 'API Integration & Testing',
      description: 'Connect backend APIs where available, test scenarios, and correct issues.',
      duration: '___ days'
    },
    {
      step: 'Phase 4',
      name: 'Deployment & Handover',
      description: 'Prepare production build, deploy to the selected environment, and hand over notes.',
      duration: '___ days'
    }
  ];
}
